import React, { useState } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import GroupIcon from "@material-ui/icons/Group";

import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { updateGroupPhoto } from "../../services/groupService";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  uploadArea: {
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(4),
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.3s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: theme.palette.action.hover,
    },
  },
  currentPhoto: {
    marginBottom: theme.spacing(2),
  },
  groupAvatar: {
    width: theme.spacing(12),
    height: theme.spacing(12),
  },
  input: {
    display: "none",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  previewImage: {
    maxWidth: "100%",
    maxHeight: 300,
    marginTop: theme.spacing(2),
  },
}));

const ChangeGroupPhotoModal = ({ open, onClose, group, onSuccess }) => {
  const classes = useStyles();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setError(i18n.t("groupManagement.changePhotoModal.validation.fileType"));
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(i18n.t("groupManagement.changePhotoModal.validation.fileSize"));
      return;
    }

    setError("");
    setSelectedFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError(
        i18n.t("groupManagement.changePhotoModal.validation.fileRequired")
      );
      return;
    }

    setUploading(true);
    try {
      await updateGroupPhoto(group.id, group.whatsappId, selectedFile);
      toast.success(i18n.t("groupManagement.toasts.photoUpdated"));
      handleClose();
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    setError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {i18n.t("groupManagement.changePhotoModal.title")}
      </DialogTitle>
      <DialogContent dividers>
        <div className={classes.root}>
          {group?.profilePicUrl && (
            <div className={classes.currentPhoto}>
              <Typography variant="subtitle2" gutterBottom>
                {i18n.t("groupManagement.changePhotoModal.form.currentPhoto")}
              </Typography>
              <Avatar
                src={group.profilePicUrl}
                className={classes.groupAvatar}
              >
                <GroupIcon style={{ fontSize: 60 }} />
              </Avatar>
            </div>
          )}

          <input
            accept="image/*"
            className={classes.input}
            id="photo-upload"
            type="file"
            onChange={handleFileSelect}
          />
          <label htmlFor="photo-upload" style={{ width: "100%" }}>
            <div className={classes.uploadArea}>
              <CloudUploadIcon color="primary" style={{ fontSize: 48 }} />
              <Typography variant="body1" gutterBottom>
                {i18n.t("groupManagement.changePhotoModal.form.dragDrop")}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                JPG, PNG - Max 5MB
              </Typography>
            </div>
          </label>

          {preview && (
            <img src={preview} alt="Preview" className={classes.previewImage} />
          )}

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={uploading}>
          {i18n.t("groupManagement.changePhotoModal.buttons.cancel")}
        </Button>
        <div className={classes.btnWrapper}>
          <Button
            onClick={handleUpload}
            color="primary"
            variant="contained"
            disabled={!selectedFile || uploading}
          >
            {i18n.t("groupManagement.changePhotoModal.buttons.upload")}
          </Button>
          {uploading && (
            <CircularProgress size={24} className={classes.buttonProgress} />
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeGroupPhotoModal;
